import React, { useEffect } from "react";

import VideoWrapper from "@Components/activity/VideoWrapper";
import Feedback from "@Components/shared/Feedback";
import { prettifyName } from "@Utils";
import { Card, Col, Empty, Row, Select, Typography } from "antd";
import moment from "moment";
import { Helmet } from "react-helmet-async";
import useStudentSubmissions from "./hooks/useStudentSubmissions";
import { IAssessmentInstanceDetails } from "@Models/IAssessmentDetails";
import { IAssessmentView } from "./types";
import LargeSpinner from "@Components/shared/LargeSpinner";

const { Title, Text } = Typography;

interface IProps extends IAssessmentView {
    assessmentInstance: IAssessmentInstanceDetails;
}

const StudentSubmissions: React.FC<IProps> = ({ assessmentInstance, tab }) => {
    const { data, isLoading, template, submission } = useStudentSubmissions({ assessmentInstance });

    //select activity if there is only one to review
    useEffect(() => {
        if (submission?.tasks?.length === 1) {
            data?.activityInstanceId?.onSelect(submission.tasks[0].activityInstanceId);
        }
    }, [submission]);

    return (
        <article className={`student-submissions-view ${tab}-tab`}>
            <Helmet>
                <title>Assessment Submissions - Totum</title>
            </Helmet>

            <section>
                {isLoading && <LargeSpinner />}
                {!isLoading && !!submission && (
                    <Row gutter={[16, 16]} justify="center">
                        <Col xs={8}>
                            <Card className="newUI-subtle">
                                <Card.Grid className="card" hoverable={false}>
                                    <Text type="secondary">Assessor</Text>
                                    <Title level={5} className="card-stat">
                                        {submission.owner}
                                    </Title>
                                    <Text type="secondary">Submitted</Text>
                                    <Title level={5} className="card-stat">
                                        {moment(submission.submissionDate).format("MMM Do YYYY")}
                                    </Title>
                                    <Text type="secondary">Status</Text>
                                    <Title level={5} className="card-stat">
                                        {`${prettifyName(submission.status)} ${submission.onTime ? "(on time)" : ""}`}
                                    </Title>
                                </Card.Grid>
                            </Card>
                        </Col>
                        <Col xs={16}>
                            <Card className="newUI-subtle">
                                <Card.Grid className="card" hoverable={false}>
                                    <Row justify="center">
                                        <Select
                                            placeholder="Select activity..."
                                            onChange={data?.activityInstanceId?.onSelect}
                                            defaultValue={
                                                submission?.tasks?.length === 1
                                                    ? submission.tasks[0].activityInstanceId
                                                    : undefined
                                            }
                                        >
                                            {submission?.tasks?.map((task) => (
                                                <Select.Option
                                                    key={task.activityInstanceId}
                                                    value={task.activityInstanceId}
                                                    loading={data.activityInstanceId.isLoading}
                                                >
                                                    {`${task.activityLabel} (${task.count}/${task.quantity}) - ${moment(
                                                        task.creationDate
                                                    ).format("MMM Do YYYY, h:mm A")}`}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Row>
                                    <Row justify={"center"}>
                                        {!!data?.activityInstanceId?.videoActivity?.mediaUrl ? (
                                            <VideoWrapper
                                                activity={data?.activityInstanceId?.videoActivity}
                                                withoutComments
                                            />
                                        ) : (
                                            <Empty
                                                description={
                                                    <span>
                                                        {!data?.activityInstanceId?.videoActivity
                                                            ? "Select an activity to review the video"
                                                            : "No video available"}
                                                    </span>
                                                }
                                            />
                                        )}
                                    </Row>
                                </Card.Grid>
                            </Card>
                        </Col>
                        {!!template && submission.status === "completed" && (
                            <Col xs={18}>
                                <Card className="newUI-subtle">
                                    <Card.Grid className="card" hoverable={false}>
                                        <Feedback
                                            description={template.description}
                                            feedback={submission?.feedback}
                                            fields={template.fields}
                                            name={template.name}
                                        />
                                    </Card.Grid>
                                </Card>
                            </Col>
                        )}
                    </Row>
                )}
            </section>
        </article>
    );
};

export default StudentSubmissions;
